/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import Cookie from 'js-cookie';
import { AlertCircle } from 'lucide-preact';

// Core
import Motion from '@/client/components/Motion';
import useContext from '@/client/context';
import { getRecruitersAppUrl } from '@/common/config/router';

// App
import Button from '@/client/components/ui/Button';
import Input from '@/client/components/ui/Input';

/*----------------------------------
- TYPES
----------------------------------*/

export interface AuthCardProps {
    // Mode
    mode: 'login' | 'signup';
    onModeChange?: (mode: 'login' | 'signup') => void;
    showModeTabs?: boolean;

    // Dependencies (for API integration)
    api?: any;
    Router?: any;
    toast?: any;

    // Request payload extras
    signupPayload?: Record<string, any>;
    loginPayload?: Record<string, any>;

    // Callbacks
    onSuccess?: (response: any) => void;
    onError?: (error: string) => void;

    // Password setup mode
    isPasswordSetup?: boolean;
    passwordToken?: string;

    // Initial values
    initialEmail?: string;

    // Additional options
    showGoogleAuth?: boolean;
    showTroublesLink?: boolean;
    onTroublesClick?: () => void;
    postAuthRedirectPath?: string;
    collectWhatsappOnSignup?: boolean;

    // Labels
    passwordLabel?: string;
    confirmLabel?: string;
    signupSubmitLabel?: string;

    // Custom submit (if you want to handle it externally)
    customSubmit?: (data: { email: string; whatsapp?: string; password: string; confirmPassword: string }) => void;
}

/*----------------------------------
- COMPONENT
----------------------------------*/

export const AuthCard: React.FC<AuthCardProps> = ({
    mode,
    onModeChange,
    showModeTabs = true,
    signupPayload,
    loginPayload,
    onSuccess,
    onError,
    isPasswordSetup = false,
    passwordToken,
    initialEmail = '',
    showGoogleAuth = true,
    showTroublesLink = false,
    onTroublesClick,
    postAuthRedirectPath,
    collectWhatsappOnSignup = false,
    passwordLabel = 'Password',
    confirmLabel = 'Confirm password',
    signupSubmitLabel = 'Create account',
    customSubmit,
}) => {
    const isSignup = mode === 'signup';
    const { toast } = useContext();
    const recruitersAppUrl = React.useMemo(() => getRecruitersAppUrl(), []);
    const recruitersUrl = React.useCallback((path: string = '') => `${recruitersAppUrl}${path}`, [recruitersAppUrl]);

    // Form state
    const [email, setEmail] = React.useState<string>(initialEmail);
    const [whatsapp, setWhatsapp] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [confirmPassword, setConfirmPassword] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    // Load memorized email on mount
    React.useEffect(() => {
        if (passwordToken || initialEmail) return;

        if (typeof window === 'undefined') return;

        const memorisedEmail = localStorage?.getItem('auth.email');
        if (memorisedEmail && memorisedEmail !== 'undefined') setEmail(memorisedEmail);
    }, [initialEmail, passwordToken]);

    // Reset form when mode changes
    React.useEffect(() => {
        setWhatsapp('');
        setPassword('');
        setConfirmPassword('');
        setError('');
    }, [mode]);

    const getErrorMessage = (err: unknown): string => {
        if (!err) return 'An unexpected error occurred.';
        if (typeof err === 'string') return err;
        if (err instanceof Error) return err.message;
        if (typeof (err as { message?: string }).message === 'string') return (err as { message: string }).message;
        return 'An unexpected error occurred.';
    };

    const getRedirectUrl = (response: any): string | undefined => {
        const redirectUrl = response?.redirectUrl ?? response?.redirect ?? response?.redirect_url;
        return typeof redirectUrl === 'string' && redirectUrl ? redirectUrl : undefined;
    };

    const postAuthRequest = React.useCallback(async (path: string, payload: Record<string, unknown>) => {
        const response = await fetch(recruitersUrl(path), {
            method: 'POST',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(getErrorMessage(data));
        return data;
    }, [recruitersUrl]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        setError('');

        // If custom submit is provided, use it
        if (customSubmit) {
            customSubmit({
                email,
                whatsapp: collectWhatsappOnSignup ? whatsapp : undefined,
                password,
                confirmPassword,
            });
            return;
        }

        // Validate email
        if (!email) {
            const errorMsg = 'Please enter your email address';
            toast?.error?.(errorMsg);
            setError(errorMsg);
            return;
        }

        const trimmedEmail = email.trim();

        const handleAsyncError = (err: unknown) => {
            console.error('AuthCard handleSubmit error', err);
            setIsLoading(false);
            const message = getErrorMessage(err);
            setError(message);
            onError?.(message);
        };

        // Password setup flow
        if (isPasswordSetup && passwordToken) {
            if (password.length < 8) {
                setError('Your password must be at least 8 characters long.');
                return;
            }

            if (password !== confirmPassword) {
                setError('Passwords do not match.');
                return;
            }

            setIsLoading(true);

            postAuthRequest('/auth/completePasswordSetup', {
                token: passwordToken,
                password,
            })
                .then((response: any) => {
                    localStorage?.setItem('auth.email', trimmedEmail);
                    if (onSuccess) {
                        onSuccess(response);
                    } else {
                        const redirectUrl = getRedirectUrl(response) ?? recruitersUrl();
                        window.location.href = redirectUrl;
                    }
                })
                .catch(handleAsyncError);
            return;
        }

        const referrerId = Cookie.get('r');
        const affiliateLinkId = Cookie.get('affiliateLinkId') ?? undefined;

        // Signup flow
        if (isSignup) {
            const trimmedWhatsapp = whatsapp.trim();

            if (collectWhatsappOnSignup && !trimmedWhatsapp) {
                setError('Please enter your WhatsApp number.');
                return;
            }

            setIsLoading(true);

            postAuthRequest('/auth/signup', {
                ...(signupPayload || {}),
                email: trimmedEmail,
                ...(collectWhatsappOnSignup ? { whatsapp: trimmedWhatsapp } : {}),
                ...(postAuthRedirectPath ? { redirectPath: postAuthRedirectPath } : {}),
                ...(referrerId ? { referrerId } : {}),
                ...(affiliateLinkId ? { affiliateLinkId } : {}),
            })
                .then((response: any) => {
                    localStorage?.setItem('auth.email', trimmedEmail);
                    if (onSuccess) {
                        onSuccess(response);
                    } else {
                        const redirectUrl = getRedirectUrl(response) ?? recruitersUrl();
                        window.location.href = redirectUrl;
                    }
                })
                .catch(handleAsyncError);

            return;
        }

        // Login flow
        if (!password) {
            setError('Please enter your password.');
            return;
        }

        setIsLoading(true);

        postAuthRequest('/auth/login', {
            ...(loginPayload || {}),
            email: trimmedEmail,
            password,
            ...(postAuthRedirectPath ? { redirectPath: postAuthRedirectPath } : {}),
        })
            .then((response: any) => {
                localStorage?.setItem('auth.email', trimmedEmail);
                if (onSuccess) {
                    onSuccess(response);
                } else {
                    const redirectUrl = getRedirectUrl(response) ?? recruitersUrl();
                    window.location.href = redirectUrl;
                }
            })
            .catch(handleAsyncError);
    };

    const handleGoogleAuth = React.useCallback(() => {
        const params = new URLSearchParams();
        if (postAuthRedirectPath) {
            params.set('redir', postAuthRedirectPath);
            params.set('reason', 'free-trial');
        }
        const query = params.toString();
        window.location.href = recruitersUrl(`/auth/google${query ? `?${query}` : ''}`);
    }, [postAuthRedirectPath, recruitersUrl]);

    const defaultSubmitLabel = isPasswordSetup
        ? isLoading
            ? 'Saving password...'
            : 'Save password & continue'
        : isSignup
          ? isLoading
              ? 'Creating your account...'
              : signupSubmitLabel
          : isLoading
            ? 'Signing you in...'
            : 'Log in';

    const showPassword = isPasswordSetup || !isSignup;
    const showConfirmPassword = isPasswordSetup;

    const renderTabs = showModeTabs && onModeChange && (
        <div className="flex w-full rounded-full bg-gray-100 p-1">
            {(['login', 'signup'] as const).map((tab) => {
                const isActive = tab === mode;
                return (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => onModeChange(tab)}
                        disabled={isActive}
                        className={[
                            'flex-1 rounded-full px-4 py-2 text-sm font-semibold transition',
                            isActive
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-900 focus:text-gray-900',
                        ].join(' ')}
                    >
                        {tab === 'login' ? 'Log in' : 'Sign up'}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {renderTabs && <div className="mb-6">{renderTabs}</div>}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.currentTarget.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full"
                        autoComplete="off"
                        readOnly={isPasswordSetup}
                    />
                </div>

                {isSignup && !isPasswordSetup && collectWhatsappOnSignup ? (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                        <Input
                            type="tel"
                            value={whatsapp}
                            onChange={(event) => setWhatsapp(event.currentTarget.value)}
                            placeholder="+33 6 12 34 56 78"
                            required
                            className="w-full"
                            autoComplete="tel"
                        />
                    </div>
                ) : null}

                {showPassword ? (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{passwordLabel}</label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.currentTarget.value)}
                            placeholder={isPasswordSetup ? 'Create a new password' : 'Enter your password'}
                            required
                            minLength={8}
                            className="w-full"
                            autoComplete="off"
                        />
                    </div>
                ) : null}

                {showConfirmPassword ? (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{confirmLabel}</label>
                        <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.currentTarget.value)}
                            placeholder="Re-enter your password"
                            required
                            minLength={8}
                            className="w-full"
                            autoComplete="off"
                        />
                    </div>
                ) : null}

                <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white py-3 font-semibold"
                >
                    {defaultSubmitLabel}
                </Button>

                {isSignup && !isPasswordSetup ? (
                    <p className="text-xs text-gray-500 text-center">
                        By signing up, you accept our{' '}
                        <a
                            href="https://www.becrosspath.com/terms/marketplace"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 underline underline-offset-2"
                        >
                            terms and conditions of usage
                        </a>
                        .
                    </p>
                ) : null}

                {showTroublesLink && onTroublesClick && !isPasswordSetup ? (
                    <button
                        type="button"
                        onClick={onTroublesClick}
                        className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                    >
                        Forgot Password?
                    </button>
                ) : null}

                {showGoogleAuth && !isPasswordSetup ? (
                    <>
                        <div className="flex items-center gap-3 text-xs uppercase text-gray-400">
                            <span className="h-px flex-1 bg-gray-200" />
                            <span>Or</span>
                            <span className="h-px flex-1 bg-gray-200" />
                        </div>

                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleGoogleAuth}
                            className="w-full gap-3 bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                            aria-label={isSignup ? 'Sign up with Google' : 'Continue with Google'}
                        >
                            <i src="brands/google" />
                            {isSignup ? 'Sign up with Google' : 'Continue with Google'}
                        </Button>
                    </>
                ) : null}

                {error && (
                    <Motion
                        tag="div"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                    >
                        <AlertCircle size={16} />
                        {error}
                    </Motion>
                )}
            </form>
        </div>
    );
};

export default AuthCard;
